import ui from "./uiStrings.json";
import { DEFAULT_LOCALE, Locale } from "./locales";
import type { SettingsStrings } from "./crosshairInfo";

/**
 * Header, footer and search labels. Kept beside the page copy so a locale is one JSON
 * edit, and read by web/seo-content.mjs too so the prerendered nav matches the app's.
 */
export type UiStrings = {
    nav: Record<"pro" | "fun" | "community" | "generator" | "checker" | "guide" | "faq" | "contact" | "privacy", string>;
    search: string;
    sections: Record<string, string>;
    /** FAQ headings rendered by components rather than page copy, plus the home page's closing line. */
    faq: Record<"homeHeading" | "answersHeading" | "homeMore", string>;
    /** Contact form chrome — labels and toasts, so the form is not English inside a translated page. */
    contact: Record<
        | "lead"
        | "emailLabel"
        | "emailPlaceholder"
        | "subjectLabel"
        | "messageLabel"
        | "messagePlaceholder"
        | "charLimitNote"
        | "send"
        | "sending"
        | "verifyRequired"
        | "success"
        | "genericError",
        string
    > & {
        /**
         * Display labels for the subject dropdown, keyed by the canonical value the API
         * returns. The stored value stays English so the admin inbox reads consistently;
         * only the label is translated. An unknown value falls back to showing itself.
         */
        subjects: Record<string, string>;
    };
    /**
     * Crosshair detail pages. Their copy is built from database rows, so unlike every other
     * route it cannot live in pages.json — these templates stand in for it. Leaving them in
     * English gave all three locales of a crosshair one identical title and description,
     * which Yandex reported as ~630 duplicate pages and then began dropping.
     */
    detail: Record<
        | "breadcrumbRoot"
        | "title"
        | "description"
        | "pro"
        | "fun"
        | "outro"
        | "keywords"
        | "codeLabel"
        | "howTo"
        | "openInGenerator"
        | "imageAlt"
        | "notFoundTitle"
        | "notFoundDescription"
        | "loadError"
        | "retry"
        | "relatedPro"
        | "relatedFun"
        | "relatedOther"
        | "sameCodeHeading"
        | "sameCodeNote"
        | "embedHeading"
        | "embedNote"
        | "embedCopy"
        | "embedCopied"
        | "checkAnother"
        | "proTeam"
        | "proChecked"
        | "proFreeAgent"
        | "proRetired"
        | "proContentCreator",
        string
    >;
    /** The code checker tool (/valorant-crosshair-code-checker); its prose lives in pages.json. */
    checker: Record<
        | "inputLabel"
        | "placeholder"
        | "example"
        | "clear"
        | "invalid"
        | "resultHeading"
        | "resultSummary"
        | "matchesHeading"
        | "matchesNone"
        | "openInGenerator"
        | "copy"
        | "copied"
        | "copyFailed",
        string
    >;
    /** Labels for the settings decoded from a crosshair's code (src/seo/crosshairInfo.js). */
    settings: SettingsStrings;
    /** Catalog paging chrome, rendered by the component and by the prerendered body. */
    pagination: Record<"previous" | "next" | "page" | "titleSuffix" | "positionNote", string>;
};

const byLocale = ui as unknown as Record<string, UiStrings>;

export function getUiStrings(locale: Locale = DEFAULT_LOCALE): UiStrings {
    return byLocale[locale] ?? byLocale[DEFAULT_LOCALE];
}

/** Fills `{name}`-style placeholders. Unknown keys are left alone rather than blanked. */
export function fillTemplate(template: string, values: Record<string, string | number>): string {
    return template.replace(/\{(\w+)\}/g, (match, key) =>
        key in values ? String(values[key]) : match
    );
}
